/**
 * DokumentUpload — Datei-Upload in den privaten Supabase-Storage-Bucket
 * `immo-dokumente`. Selbstständige Komponente; legt nach erfolgreichem
 * Upload einen Datensatz in der Tabelle `immo_dokumente` an.
 *
 * Kein react-query, kein localStorage — nur useState (App-Standard).
 */
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { UploadCloud, Loader2 } from 'lucide-react';

const BUCKET = 'immo-dokumente';

const KATEGORIEN = [
  'allgemein', 'versorger', 'zaehler', 'mietvertrag', 'nebenkosten',
  'kaufvertrag', 'grundbuch', 'sanierung', 'sonstiges',
];

/** Dateinamen Storage-sicher machen (Leer-/Sonderzeichen ersetzen). */
function safeFileName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || 'datei';
}

/** Signierte URL erzeugen und Dokument in neuem Tab öffnen. */
export async function oeffneDokument(bucket: string, path: string) {
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
    if (error) { toast.error(error.message); return; }
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    } else {
      toast.error('Konnte Dokument nicht öffnen');
    }
  } catch (err: any) {
    toast.error(err?.message || 'Konnte Dokument nicht öffnen');
  }
}

export default function DokumentUpload({
  objektId,
  objektName,
  onUploaded,
}: {
  objektId?: string;
  objektName?: string;
  onUploaded?: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [kategorie, setKategorie] = useState('allgemein');
  const [busy, setBusy] = useState(false);

  async function upload() {
    if (!file) { toast.error('Bitte zuerst eine Datei wählen'); return; }
    setBusy(true);
    try {
      const safeName = safeFileName(file.name);
      const path = `${objektId ?? 'allgemein'}/${Date.now()}_${safeName}`;

      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file);
      if (upErr) { toast.error(upErr.message); return; }

      const { error: insErr } = await supabase.from('immo_dokumente').insert({
        objekt_id: objektId ?? null,
        titel: file.name,
        kategorie,
        datum: new Date().toISOString().slice(0, 10),
        dateiname: file.name,
        storage_bucket: BUCKET,
        storage_path: path,
      });
      if (insErr) { toast.error(insErr.message); return; }

      toast.success('Dokument hochgeladen');
      setFile(null);
      // File-Input zurücksetzen
      const input = document.getElementById('immo-dok-file') as HTMLInputElement | null;
      if (input) input.value = '';
      onUploaded?.();
    } catch (err: any) {
      toast.error(err?.message || 'Upload fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <UploadCloud className="w-4 h-4 text-zinc-400" />
        <p className="text-sm font-semibold text-zinc-800">
          Dokument hochladen{objektName ? ` · ${objektName}` : ''}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block md:col-span-2">
          <span className="text-xs text-zinc-500">Datei</span>
          <input
            id="immo-dok-file"
            type="file"
            accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.txt"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="mt-1 w-full text-sm text-zinc-700 border border-zinc-200 rounded-lg px-3 py-2 bg-white file:mr-3 file:rounded-md file:border-0 file:bg-zinc-100 file:px-3 file:py-1 file:text-sm file:text-zinc-700 hover:file:bg-zinc-200 focus:outline-none focus:border-amber-400"
          />
        </label>

        <label className="block">
          <span className="text-xs text-zinc-500">Kategorie</span>
          <select
            value={kategorie}
            onChange={(e) => setKategorie(e.target.value)}
            className="mt-1 w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-amber-400"
          >
            {KATEGORIEN.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
      </div>

      <button
        onClick={upload}
        disabled={busy}
        className="inline-flex items-center gap-2 text-sm font-semibold text-white px-4 py-2 rounded-lg disabled:opacity-60"
        style={{ backgroundColor: '#059669' }}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
        {busy ? 'Lädt…' : 'Hochladen'}
      </button>
    </div>
  );
}
